import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquareText, Send, Users, BarChart3, Settings2, Plus, Trash2,
  Loader2, RefreshCw, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight,
  Upload, Phone, Search, X, Calendar, Building2, BookOpen, Zap, Info,
  DollarSign, AlertCircle, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import SMSContactsTab from '../../components/sms/SMSContactsTab';

const TABS = [
  { id: 'dashboard', label: 'Dashboard',  icon: BarChart3 },
  { id: 'send',      label: 'Send SMS',   icon: Send },
  { id: 'contacts',  label: 'Contacts',   icon: Users },
  { id: 'campaigns', label: 'Campaigns',  icon: MessageSquareText },
  { id: 'requests',  label: 'Access Requests', icon: BookOpen },
  { id: 'settings',  label: 'Settings',   icon: Settings2 },
];

const STATUS_COLORS = {
  sent:      'bg-blue-100 text-blue-700',
  scheduled: 'bg-violet-100 text-violet-700',
  sending:   'bg-amber-100 text-amber-700',
  failed:    'bg-red-100 text-red-700',
  partial:   'bg-orange-100 text-orange-700',
};

function StatCard({ label, value, sub, color = 'blue', icon: Icon }) {
  const colors = {
    blue:    'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
    red:     'from-red-500 to-red-600',
    violet:  'from-violet-500 to-violet-600',
    amber:   'from-amber-500 to-amber-600',
    teal:    'from-teal-500 to-teal-600',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-extrabold text-slate-900 mt-0.5">{value ?? '—'}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── DASHBOARD TAB ────────────────────────────────────────────────────────
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    api.get('/sms/analytics').then(r => setStats(r.data)).catch(() => {});
    api.get('/sms/balance').then(r => setBalance(r.data)).catch(() => {});
  }, []);

  const rate = stats?.total_sent ? Math.round((stats.total_delivered / stats.total_sent) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Total Sent" value={stats?.total_sent ?? 0} icon={Send} color="blue" sub="All time" />
        <StatCard label="Delivered" value={stats?.total_delivered ?? 0} icon={CheckCircle} color="emerald" sub={`${rate}% delivery rate`} />
        <StatCard label="Failed" value={stats?.total_failed ?? 0} icon={XCircle} color="red" sub="Undelivered" />
        <StatCard label="Campaigns" value={stats?.total_campaigns ?? 0} icon={MessageSquareText} color="violet" />
        <StatCard label="Account Balance" value={balance?.balance ? `${balance.balance} units` : (balance?.configured ? 'Fetching…' : 'Not configured')} icon={Zap} color="amber" sub="BulkSMSLive units" />
        <StatCard label="Pending Requests" value={stats?.pending_access_requests ?? 0} icon={BookOpen} color="teal" sub="Awaiting approval" />
      </div>

      {!balance?.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold text-amber-800 text-sm">BulkSMSLive not configured</p>
            <p className="text-xs text-amber-700 mt-0.5">Add your API key in Integrations → BulkSMSLive to enable SMS sending.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SEND SMS TAB ─────────────────────────────────────────────────────────
function SendSMSTab() {
  const [form, setForm]           = useState({ message: '', sender_name: '', campaign_name: '', recipient_type: 'contacts', schedule_at: '' });
  const [contacts, setContacts]   = useState([]);
  const [schools, setSchools]     = useState([]);
  const [groups, setGroups]       = useState([]);
  const [selected, setSelected]   = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [manualNums, setManualNums] = useState('');
  const [sending, setSending]     = useState(false);

  useEffect(() => {
    api.get('/sms/contacts?limit=100&page=1').then(r => setContacts(r.data || [])).catch(() => {});
    api.get('/master/schools-list').then(r => setSchools(r.data?.schools || [])).catch(() => {});
    api.get('/sms/groups').then(r => setGroups(r.data || [])).catch(() => {});
  }, []);

  const charCount = form.message.length;
  const smsCount  = Math.ceil(charCount / 160) || 1;
  const selGroup  = groups.find(g => g.id === selectedGroup);
  const recipientCount = form.recipient_type === 'contacts' ? selected.length
    : form.recipient_type === 'schools' ? selected.length
    : form.recipient_type === 'group' ? (selGroup ? '?' : 0)
    : manualNums.split('\n').filter(l => l.trim()).length;

  const handleSend = async () => {
    if (!form.message.trim()) return toast.error('Message is required');
    if (form.recipient_type === 'group' && !selectedGroup) return toast.error('Select a group');
    if (form.recipient_type !== 'group' && recipientCount === 0) return toast.error('Select at least one recipient');
    setSending(true);
    try {
      const payload = {
        message: form.message,
        sender_name: form.sender_name || undefined,
        campaign_name: form.campaign_name || undefined,
        schedule_at: form.schedule_at || undefined,
        recipient_type: form.recipient_type,
        contact_ids: form.recipient_type === 'contacts' ? selected : [],
        school_ids: form.recipient_type === 'schools' ? selected : [],
        group_id: form.recipient_type === 'group' ? selectedGroup : undefined,
        manual_numbers: form.recipient_type === 'manual' ? manualNums.split('\n').filter(l => l.trim()) : [],
      };
      const r = await api.post('/sms/send', payload);
      toast.success(r.message || 'SMS sent!');
      setForm({ message: '', sender_name: '', campaign_name: '', recipient_type: 'contacts', schedule_at: '' });
      setSelected([]);
      setSelectedGroup('');
      setManualNums('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Compose */}
      <div className="lg:col-span-3 space-y-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h3 className="font-bold text-slate-800">Compose Message</h3>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Campaign Name <span className="font-normal text-slate-400">(optional)</span></label>
            <input value={form.campaign_name} onChange={e => setForm(f => ({...f, campaign_name: e.target.value}))}
              placeholder="e.g. October School Notice" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Sender Name <span className="font-normal text-slate-400">(max 11 chars)</span></label>
            <input value={form.sender_name} onChange={e => setForm(f => ({...f, sender_name: e.target.value.slice(0,11)}))}
              placeholder="Eduva SMS" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400 font-mono" />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Message</label>
            <textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))}
              rows={5} placeholder="Type your SMS message here…"
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-400 resize-none" />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">{charCount} chars</span>
              <span className="text-xs text-slate-400">{smsCount} SMS × {recipientCount} recipients = <strong className="text-slate-600">{smsCount * recipientCount} units</strong></span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Schedule <span className="font-normal text-slate-400">(leave blank to send now)</span></label>
            <input type="datetime-local" value={form.schedule_at}
              onChange={e => setForm(f => ({...f, schedule_at: e.target.value}))}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
          </div>

          <button onClick={handleSend} disabled={sending || !form.message.trim() || recipientCount === 0}
            className="w-full py-3 rounded-xl font-bold text-white bg-[#2563eb] hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
            data-testid="send-sms-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {form.schedule_at ? 'Schedule SMS' : `Send${form.recipient_type==='group' && selGroup ? ` to "${selGroup.name}"` : recipientCount > 0 ? ` to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}` : ''}`}
          </button>
        </div>
      </div>

      {/* Recipients */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-3">
          <h3 className="font-bold text-slate-800">Recipients</h3>

          {/* Type toggle */}
          <div className="flex rounded-xl bg-slate-100 p-1 gap-1 flex-wrap">
            {[['contacts','Contacts'],['group','Group'],['schools','Schools'],['manual','Manual']].map(([v,l]) => (
              <button key={v} onClick={() => { setForm(f => ({...f, recipient_type: v})); setSelected([]); setSelectedGroup(''); }}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${form.recipient_type===v?'bg-white shadow text-slate-800':'text-slate-500'}`}
                data-testid={`recipient-${v}`}>
                {l}
              </button>
            ))}
          </div>

          {form.recipient_type === 'group' && (
            <div className="space-y-2">
              <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
                data-testid="group-select">
                <option value="">— Select a group —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              {selectedGroup && (
                <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
                  All contacts in "<strong>{selGroup?.name}</strong>" will receive this SMS.
                </p>
              )}
              {groups.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-4">No groups yet — create them in the Contacts tab</p>
              )}
            </div>
          )}

          {form.recipient_type === 'contacts' && (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-slate-500">{selected.length}/{contacts.length} selected</span>
                <button onClick={() => setSelected(contacts.map(c=>c.id))} className="text-xs text-blue-600 font-semibold">All</button>
              </div>
              {contacts.map(c => (
                <label key={c.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="rounded" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{c.name || 'Unnamed'}</p>
                    <p className="text-xs text-slate-400 font-mono">{c.phone}</p>
                  </div>
                </label>
              ))}
              {contacts.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No contacts saved yet</p>}
            </div>
          )}

          {form.recipient_type === 'schools' && (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-slate-500">{selected.length}/{schools.length} selected</span>
                <button onClick={() => setSelected(schools.map(s=>s.id))} className="text-xs text-blue-600 font-semibold">All</button>
              </div>
              {schools.map(s => (
                <label key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} className="rounded" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{s.name}</p>
                    <p className="text-xs text-slate-400">{s.state} · {s.city}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {form.recipient_type === 'manual' && (
            <div>
              <textarea value={manualNums} onChange={e => setManualNums(e.target.value)}
                rows={8} placeholder={"One number per line:\n08012345678\n08087654321"}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono outline-none focus:border-blue-400 resize-none" />
              <p className="text-xs text-slate-400 mt-1">{manualNums.split('\n').filter(l=>l.trim()).length} numbers</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CONTACTS TAB — replaced by shared component ───────────────────────────
function ContactsTab() {
  return <SMSContactsTab contactsApiBase="/sms" />;
}

// ── CAMPAIGNS TAB ────────────────────────────────────────────────────────
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sms/campaigns?page=${page}&limit=10`);
      setCampaigns(r.data || []);
      setTotal(r.total || 0);
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (id) => {
    try {
      await api.get(`/sms/campaigns/${id}/refresh`);
      toast.success('Delivery status refreshed');
      load();
    } catch { toast.error('Failed to refresh'); }
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{total} campaigns</p>
        <button onClick={load} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No campaigns yet</div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid={`campaign-row-${c.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-bold text-slate-800">{c.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {c.sent_at ? new Date(c.sent_at).toLocaleString('en-NG') : c.scheduled_at ? `Scheduled: ${new Date(c.scheduled_at).toLocaleString('en-NG')}` : 'Pending'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-500'}`}>
                    {c.status}
                  </span>
                  <button onClick={() => refresh(c.id)} className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg" title="Refresh delivery" data-testid={`refresh-campaign-${c.id}`}>
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-2.5 mb-3 line-clamp-2">{c.message}</p>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5 text-blue-600 font-semibold"><Send className="w-3 h-3" /> {c.stats?.sent ?? 0} Sent</span>
                <span className="flex items-center gap-1.5 text-emerald-600 font-semibold"><CheckCircle className="w-3 h-3" /> {c.stats?.delivered ?? 0} Delivered</span>
                <span className="flex items-center gap-1.5 text-red-500 font-semibold"><XCircle className="w-3 h-3" /> {c.stats?.failed ?? 0} Failed</span>
                <span className="ml-auto text-slate-400">Sender: <strong>{c.sender_name}</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p=>Math.max(1,p-1))} disabled={page===1} className="p-2 rounded-xl border border-slate-200 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-slate-600">{page}/{totalPages}</span>
          <button onClick={() => setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="p-2 rounded-xl border border-slate-200 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

// ── ACCESS REQUESTS TAB ──────────────────────────────────────────────────
function AccessRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter]     = useState('pending');
  const [loading, setLoading]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sms/access-requests?status=${filter}`);
      setRequests(r.data || []);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    await api.post(`/sms/access-requests/${id}/approve`);
    toast.success('Access approved');
    load();
  };

  const reject = async (id) => {
    await api.post(`/sms/access-requests/${id}/reject`, { reason: '' });
    toast.success('Request rejected');
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {['pending','approved','rejected','all'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${filter===s?'bg-[#2563eb] text-white':'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {s}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-slate-400">No {filter} requests</div>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-slate-800">{r.school_name || r.partner_name || r.requester_id}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${r.status==='pending'?'bg-amber-100 text-amber-700':r.status==='approved'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-600'}`}>
                    {r.status}
                  </span>
                </div>
                <p className="text-xs text-slate-400">{r.requester_type} · Requested {new Date(r.requested_at).toLocaleDateString('en-NG')}</p>
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-2">
                  <button onClick={() => approve(r.id)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-all">Approve</button>
                  <button onClick={() => reject(r.id)} className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs font-bold hover:bg-red-50 transition-all">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SETTINGS TAB ─────────────────────────────────────────────────────────
function SettingsTab() {
  const [p, setP]       = useState({ price_per_credit_naira: 2, credits_per_sms_page: 1, transaction_fee_naira: 0, min_topup_units: 100 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/sms/pricing').then(r => { if (r.data) setP(r.data); }).catch(() => {});
  }, []);

  const set = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  const savePricing = async () => {
    setSaving(true);
    try {
      await api.put('/sms/pricing', {
        price_per_credit_naira: parseFloat(p.price_per_credit_naira),
        credits_per_sms_page:   parseInt(p.credits_per_sms_page),
        transaction_fee_naira:  parseFloat(p.transaction_fee_naira),
        min_topup_units:        parseInt(p.min_topup_units),
      });
      toast.success('SMS pricing saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const previewUnits = 1000;
  const subtotal = previewUnits * parseFloat(p.price_per_credit_naira || 0);
  const total    = subtotal + parseFloat(p.transaction_fee_naira || 0);

  return (
    <div className="max-w-xl space-y-6">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div>
          <h3 className="font-bold text-slate-800 mb-0.5">SMS Credit Pricing</h3>
          <p className="text-xs text-slate-500">Configure how schools and partners buy and consume SMS credits.</p>
        </div>

        {/* Price per credit */}
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Price per Credit (₦)</label>
          <p className="text-xs text-slate-400 mb-2">How much ₦ one SMS credit costs. e.g. ₦2 means 1000 units = ₦2000</p>
          <input type="number" min="0.01" step="0.01" value={p.price_per_credit_naira}
            onChange={e => set('price_per_credit_naira', e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
            data-testid="price-per-credit-input" />
        </div>

        {/* Credits per SMS page */}
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Credits per SMS Page (160 chars)</label>
          <p className="text-xs text-slate-400 mb-2">Credits deducted per recipient per 160-character SMS page. e.g. 2 means a 160-char SMS to 10 people = 20 credits</p>
          <input type="number" min="1" step="1" value={p.credits_per_sms_page}
            onChange={e => set('credits_per_sms_page', e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
            data-testid="credits-per-sms-input" />
        </div>

        {/* Transaction fee */}
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Transaction Fee (₦) — per topup</label>
          <p className="text-xs text-slate-400 mb-2">Flat fee added to every top-up payment. Enter 0 for no fee.</p>
          <input type="number" min="0" step="0.01" value={p.transaction_fee_naira}
            onChange={e => set('transaction_fee_naira', e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
            data-testid="transaction-fee-input" />
        </div>

        {/* Min topup */}
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Minimum Top-up (units)</label>
          <p className="text-xs text-slate-400 mb-2">Minimum number of credits a school/partner must buy at once.</p>
          <input type="number" min="1" step="1" value={p.min_topup_units}
            onChange={e => set('min_topup_units', e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
            data-testid="min-topup-input" />
        </div>

        {/* Live preview */}
        <div className="bg-blue-50 rounded-2xl p-4 space-y-2 text-sm border border-blue-100">
          <p className="font-bold text-blue-900">Pricing preview — {previewUnits.toLocaleString()} credits</p>
          <div className="grid grid-cols-2 gap-x-4 text-blue-800 text-xs">
            <span>Number of units:</span>      <span className="font-semibold">{previewUnits.toLocaleString()}</span>
            <span>Unit price:</span>           <span className="font-semibold">₦{parseFloat(p.price_per_credit_naira||0).toFixed(2)}</span>
            <span>Amount to pay:</span>        <span className="font-semibold">₦{subtotal.toFixed(2)}</span>
            <span>Transaction fee:</span>      <span className="font-semibold">₦{parseFloat(p.transaction_fee_naira||0).toFixed(2)}</span>
            <span className="font-bold">Total payment:</span> <span className="font-bold text-blue-900">₦{total.toFixed(2)}</span>
          </div>
          <div className="border-t border-blue-100 pt-2 mt-1">
            <p className="text-blue-700">1 SMS (160 chars) to 10 people = <strong>{parseInt(p.credits_per_sms_page||1) * 10} credits</strong></p>
            <p className="text-blue-700">1 SMS (320 chars) to 10 people = <strong>{parseInt(p.credits_per_sms_page||1) * 10 * 2} credits</strong></p>
          </div>
        </div>

        <button onClick={savePricing} disabled={saving}
          className="w-full py-2.5 rounded-xl bg-[#2563eb] text-white font-bold text-sm hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
          data-testid="save-pricing-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save Pricing Settings
        </button>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-start gap-3">
        <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-slate-600">To update your BulkSMSLive API key or sender name, go to <strong>Integrations & Credentials → BulkSMSLive</strong>.</p>
      </div>
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────
export default function MasterSMSPage() {
  const [tab, setTab] = useState('dashboard');

  return (
    <div className="px-6 md:px-8 py-6 md:py-8" data-testid="master-sms-page">
      {/* Dark hero header */}
      <div className="relative bg-[#0f172a] rounded-3xl overflow-hidden mb-7" style={{ minHeight: 120 }}>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
        <div className="relative p-7 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
              <MessageSquareText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-white tracking-tight">SMS Platform</h1>
              <p className="text-slate-400 text-sm mt-0.5">Send bulk SMS, manage contacts and monitor delivery</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <div className="bg-slate-100 rounded-2xl p-1 flex gap-1 mb-7 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${tab===t.id?'bg-white text-slate-800 shadow':'text-slate-500 hover:text-slate-700'}`}
            data-testid={`sms-tab-${t.id}`}>
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'send'      && <SendSMSTab />}
      {tab === 'contacts'  && <ContactsTab />}
      {tab === 'campaigns' && <CampaignsTab />}
      {tab === 'requests'  && <AccessRequestsTab />}
      {tab === 'settings'  && <SettingsTab />}
    </div>
  );
}
